import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentFormPage from './pages/DocumentFormPage';
import MasterdataPage from './pages/MasterdataPage';
import FormConfigPage from './pages/FormConfigPage';
import ApprovalConfigPage from './pages/ApprovalConfigPage';
import RolesPage from './pages/RolesPage';
import MembersPage from './pages/MembersPage';
import SettingsPage from './pages/SettingsPage';
import Layout from './components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  en: {
    overview: 'Overview',
    dashboard: 'Dashboard',
    documents: 'Documents',
    settings: 'Settings',
    masterdata: 'Masterdata',
    formConfig: 'Form Config',
    approvalConfig: 'Approval Config',
    roles: 'Roles',
    members: 'Members',
    brandSettings: 'Brand Settings',
    login: 'Login',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    sendResetLink: 'Send Reset Link',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    submit: 'Submit',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    view: 'View',
    search: 'Search',
    filter: 'Filter',
    status: 'Status',
    draft: 'Draft',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    approve: 'Approve',
    reject: 'Reject',
    actions: 'Actions',
    name: 'Name',
    description: 'Description',
    permissions: 'Permissions',
    role: 'Role',
    invite: 'Invite',
    inviteMember: 'Invite Member',
    primaryColor: 'Primary Color',
    language: 'Language',
    total: 'Total',
    active: 'Active',
    users: 'Users',
    recentDocuments: 'Recent Documents',
    documentsByStatus: 'Documents by Status',
    documentsByCategory: 'Documents by Category',
    noData: 'No data available',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    welcomeBack: 'Welcome back',
    signInToContinue: 'Sign in to continue to DDS',
    rememberMe: 'Remember me',
    createAccount: 'Create Account',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    register: 'Register',
    fullName: 'Full Name',
    addNew: 'Add New',
    field: 'Field',
    fields: 'Fields',
    type: 'Type',
    text: 'Text',
    number: 'Number',
    required: 'Required',
    optional: 'Optional',
    selectOption: 'Select an option',
    uploadFile: 'Upload File',
    maxFileSize: 'Max file size: 2MB',
    onlyPdf: 'Only PDF files allowed',
    approvalSteps: 'Approval Steps',
    step: 'Step',
    approver: 'Approver',
    comment: 'Comment',
    history: 'History',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    createdBy: 'Created By',
    all: 'All',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    of: 'of',
    showing: 'Showing',
    entries: 'entries',
    noResults: 'No results found',
  },
  id: {
    overview: 'Overview',
    dashboard: 'Dasbor',
    documents: 'Dokumen',
    settings: 'Pengaturan',
    masterdata: 'Data Master',
    formConfig: 'Konfigurasi Form',
    approvalConfig: 'Konfigurasi Persetujuan',
    roles: 'Peran',
    members: 'Anggota',
    brandSettings: 'Pengaturan Brand',
    login: 'Masuk',
    logout: 'Keluar',
    email: 'Email',
    password: 'Kata Sandi',
    forgotPassword: 'Lupa Kata Sandi?',
    resetPassword: 'Reset Kata Sandi',
    sendResetLink: 'Kirim Link Reset',
    newPassword: 'Kata Sandi Baru',
    confirmPassword: 'Konfirmasi Kata Sandi',
    submit: 'Kirim',
    save: 'Simpan',
    cancel: 'Batal',
    delete: 'Hapus',
    edit: 'Edit',
    create: 'Buat',
    view: 'Lihat',
    search: 'Cari',
    filter: 'Filter',
    status: 'Status',
    draft: 'Draf',
    pending: 'Menunggu',
    approved: 'Disetujui',
    rejected: 'Ditolak',
    approve: 'Setujui',
    reject: 'Tolak',
    actions: 'Aksi',
    name: 'Nama',
    description: 'Deskripsi',
    permissions: 'Izin',
    role: 'Peran',
    invite: 'Undang',
    inviteMember: 'Undang Anggota',
    primaryColor: 'Warna Utama',
    language: 'Bahasa',
    total: 'Total',
    active: 'Aktif',
    users: 'Pengguna',
    recentDocuments: 'Dokumen Terbaru',
    documentsByStatus: 'Dokumen berdasarkan Status',
    documentsByCategory: 'Dokumen berdasarkan Kategori',
    noData: 'Tidak ada data',
    loading: 'Memuat...',
    error: 'Error',
    success: 'Berhasil',
    welcomeBack: 'Selamat datang kembali',
    signInToContinue: 'Masuk untuk melanjutkan ke DDS',
    rememberMe: 'Ingat saya',
    createAccount: 'Buat Akun',
    alreadyHaveAccount: 'Sudah punya akun?',
    dontHaveAccount: 'Belum punya akun?',
    register: 'Daftar',
    fullName: 'Nama Lengkap',
    addNew: 'Tambah Baru',
    field: 'Field',
    fields: 'Fields',
    type: 'Tipe',
    text: 'Teks',
    number: 'Angka',
    required: 'Wajib',
    optional: 'Opsional',
    selectOption: 'Pilih opsi',
    uploadFile: 'Unggah File',
    maxFileSize: 'Ukuran maks: 2MB',
    onlyPdf: 'Hanya file PDF yang diizinkan',
    approvalSteps: 'Langkah Persetujuan',
    step: 'Langkah',
    approver: 'Penyetuju',
    comment: 'Komentar',
    history: 'Riwayat',
    createdAt: 'Dibuat Pada',
    updatedAt: 'Diperbarui Pada',
    createdBy: 'Dibuat Oleh',
    all: 'Semua',
    previous: 'Sebelumnya',
    next: 'Selanjutnya',
    page: 'Halaman',
    of: 'dari',
    showing: 'Menampilkan',
    entries: 'entri',
    noResults: 'Tidak ada hasil',
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const useTranslation = () => {
  const { language } = useLanguage();
  return (key) => translations[language]?.[key] || translations.en[key] || key;
};

// Helper function to parse API errors
export const parseApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || e.message || String(e)).join(', ');
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return error.message || 'An error occurred';
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');

  const changeLanguage = (lang) => {
    localStorage.setItem('language', lang);
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Settings Context for brand colors
const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within SettingsProvider');
  return context;
};

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    primary_color: '#C8102E',
    dark_color: '#A50E27',
    soft_color: '#F8D7DA'
  });

  const fetchSettings = async () => {
    try {
      // Use public endpoint that doesn't require authentication
      const response = await axios.get(`${API}/settings/public`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        applyColors(response.data);
      }
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const applyColors = (colors) => {
    const root = document.documentElement;
    if (colors.primary_color) {
      root.style.setProperty('--color-primary', colors.primary_color);
      const hsl = hexToHSL(colors.primary_color);
      root.style.setProperty('--primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      root.style.setProperty('--ring', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    }
    if (colors.dark_color) {
      root.style.setProperty('--color-primary-dark', colors.dark_color);
      const hslDark = hexToHSL(colors.dark_color);
      root.style.setProperty('--chart-1', `${hslDark.h} ${hslDark.s}% ${hslDark.l}%`);
    }
    if (colors.soft_color) {
      root.style.setProperty('--color-primary-soft', colors.soft_color);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    applyColors(newSettings);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Helper function to convert hex to HSL
const hexToHSL = (hex) => {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/overview" replace />;
  }

  return children;
};

function App() {
  return (
    <LanguageProvider>
      <SettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><LoginPage isRegister /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

              {/* Protected Routes */}
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/overview" replace />} />
                <Route path="overview" element={<DashboardPage />} />
                <Route path="dashboard" element={<Navigate to="/overview" replace />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="documents/new" element={<DocumentFormPage />} />
                <Route path="documents/:id" element={<DocumentFormPage />} />
                <Route path="settings/masterdata" element={<MasterdataPage />} />
                <Route path="settings/form-config" element={<FormConfigPage />} />
                <Route path="settings/approval-config" element={<ApprovalConfigPage />} />
                <Route path="settings/roles" element={<RolesPage />} />
                <Route path="settings/members" element={<MembersPage />} />
                <Route path="settings/brand" element={<SettingsPage />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/overview" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </SettingsProvider>
    </LanguageProvider>
  );
}

export default App;
